import { Dispatch, SetStateAction, useEffect } from 'react';
import { IBoundary } from '@domain/regulations/hooks/useRegulationBoundaryAtLocation';
import { useSpeciesRegulationAtLocation } from '@domain/regulations/hooks/useSpeciesRegulationAtLocation';
import { getAdditionalInformation } from '@domain/regulations/getAdditionalInformation';
import { isProhibitedLocation } from '@domain/regulations/isProhibitedLocation';
import { ICoordinates } from '@app-types/geo';
import { Accordion, IAccordionData } from '../../Accordion/Accordion';
import { RegulationCards } from '../../RegulationCards/RegulationCards';
import { RegulationsLoader } from './RegulationsLoader';
import { Regulations } from '../../../api/fishrules/getSpeciesRegulationsAtLocation';
import { RegulationInfo } from '../RegulationSection';

export interface IRegulationDetailsProps {
  boundaries: IBoundary[] | undefined;
  coordinates: ICoordinates;
  selectedSpeciesId?: number;
  initialRegulations?: Regulations;
  onRegulationChange: Dispatch<SetStateAction<RegulationInfo>>;
}

export const RegulationDetails = ({
  boundaries,
  coordinates,
  selectedSpeciesId,
  initialRegulations,
  onRegulationChange,
}: IRegulationDetailsProps) => {
  const isInitialSpecies = selectedSpeciesId === initialRegulations?.fish_id;
  const shouldFetch = !isInitialSpecies && selectedSpeciesId !== undefined;

  const {
    isLoading,
    regulation: fetchedRegulation,
    fetchedAt,
  } = useSpeciesRegulationAtLocation(
    shouldFetch ? { fishrulesSpeciesId: selectedSpeciesId, ...coordinates } : undefined,
  );

  const regulation = isInitialSpecies ? initialRegulations : fetchedRegulation;
  const areSpeciesLoading = shouldFetch && isLoading;

  const { isProhibited, message } = isProhibitedLocation(boundaries ?? []);

  useEffect(() => {
    onRegulationChange(prev => ({
      boundaryName: regulation?.location_name ?? prev.boundaryName,
      fetchedAt: fetchedAt ?? prev.fetchedAt,
      isLoading: areSpeciesLoading,
    }));
  }, [regulation, fetchedAt, areSpeciesLoading, onRegulationChange]);

  const blocksData: IAccordionData[] = [];
  if (regulation && boundaries) {
    const additionalInformation = getAdditionalInformation({ regulation, boundaries });
    additionalInformation.forEach(({ visible, title: additionaTitle, body }) => {
      if (visible) {
        blocksData.push({ title: additionaTitle, body, id: additionaTitle });
      }
    });
  }

  if (shouldFetch && !fetchedRegulation && !isLoading) {
    return <></>;
  }

  if (areSpeciesLoading || !regulation) {
    return <RegulationsLoader />;
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 md:grid-cols-3">
        <RegulationCards
          coordinates={coordinates}
          regulation={regulation}
          infoCardSize="lg"
          isProhibitedLocation={isProhibited}
          prohibitedMessage={message}
        />
      </div>
      <div className="mt-10 grid grid-cols-12 gap-4 sm:grid-cols-4 md:grid-cols-12">
        <Accordion
          className="bg-sea-foam col-span-full sm:col-span-3 md:col-span-full"
          blocksData={blocksData}
          contentClassName="overflow-auto!"
        />
      </div>
    </>
  );
};
